import React from 'react';
import PropTypes from 'prop-types';
import 'react-dates/initialize';

import isMobile from '../util/mobile.js';
import { calendarPhrases } from '../locale/phrases.js';
import { momentWithLocale as moment } from '../locale/i18n.js';
import { adjustedCalendarWidth } from './calendarWidthHelper.js';
import { disableBodyScroll, enableBodyScroll } from '../util/bodyScroll.js';
import { formats } from '../locale/datetime';

import asyncImport from '../async/AsyncImport.jsx';
import CalendarTopBar from './CalendarTopBar.jsx';
import DateRangeCalendarPlaceholder from './DateRangeCalendarPlaceholder.jsx';

const AsyncDateRangePicker = asyncImport(() => import('react-dates/lib/components/DateRangePicker.js'));

export default class DateRangeCalendar extends React.Component {
    constructor(props) {
        super(props);

        this.onDateChange = this.onDateChange.bind(this);
        this.onFocusChange = this.onFocusChange.bind(this);
        this.clearCalendar = this.clearCalendar.bind(this);
        this.closeCalendar = this.closeCalendar.bind(this);
        this.transitionOverlay = this.transitionOverlay.bind(this);
        this.calendarInfoPosition = this.calendarInfoPosition.bind(this);

        this.mobile = isMobile();
        this.state = {
            focusedInput: window.location.search === `?calendar=${this.props.startId}` ? 'startDate' : null,
        };

        window.addEventListener('popstate', this.transitionOverlay);
    }

    componentDidMount() {
        if (this.mobile && this.state.focusedInput === 'startDate') {
            disableBodyScroll();
        } else {
            enableBodyScroll();
        }
    }

    componentWillUpdate() {
        if (this.state.focusedInput === 'startDate' && document.getElementsByClassName('DayPicker_transitionContainer').length > 0) {
            const scrollPos = document.getElementsByClassName('DayPicker_transitionContainer')[0].scrollTop;
            window.sessionStorage.setItem('scrollPos', scrollPos);
        }
    }

    componentDidUpdate() {
        if (this.state.focusedInput === 'endDate' && document.getElementsByClassName('DayPicker_transitionContainer').length > 0) {
            document.getElementsByClassName('DayPicker_transitionContainer')[0].scrollTop = window.sessionStorage.getItem('scrollPos');
        }
    }

    componentWillUnmount() {
        window.removeEventListener('popstate', this.transitionOverlay);
    }

    transitionOverlay() {
        if (this.mobile) {
            if (window.history.state == null) {
                // This means it's page load
                return;
            }
            this.setState({ focusedInput: window.location.search === `?calendar=${this.props.startId}` ? 'startDate' : null });
        }
    }

    onDateChange({ startDate, endDate }) {
        if (startDate) {
            this.props.onStartDateChange(startDate);
        }
        if (endDate && (startDate instanceof moment ? !startDate.isBefore(this.props.startDate) : true)) {
            this.props.onEndDateChange(endDate);
        }
    }

    onFocusChange(focusedInput) {
        if (focusedInput) {
            this.props.onFocusChange({ calendarId: this.props.startId });
        }
        this.setState({ focusedInput });
        if (focusedInput === 'startDate' && this.mobile) {
            disableBodyScroll();
            if (this.props.pushUrlContext && window.location.search !== `?calendar=${this.props.startId}`) {
                this.props.pushUrlContext(`calendar=${this.props.startId}`);
            }
        }
        if (focusedInput === null && this.mobile) {
            enableBodyScroll();
            if (this.props.popUrlContext) {
                this.props.popUrlContext();
            }
        }
    }

    clearCalendar() {
        this.setState({ focusedInput: 'startDate' });
        this.props.resetDepartureDate(null);
        this.props.resetReturnDate(null);
    }

    closeCalendar() {
        this.setState({ focusedInput: null });
        if (this.mobile) {
            window.sessionStorage.setItem('scrollPos', 0);
            if (this.props.popUrlContext) {
                this.props.popUrlContext();
            }
        }
    }

    calendarInfoPosition() {
        if (this.mobile) {
            return 'top';
        }
        return this.props.calendarInfo ? 'bottom' : null;
    }

    renderCalendarInfo() {
        return this.mobile ? (
            <CalendarTopBar
                closeCalendar={this.closeCalendar}
                clearCalendar={this.clearCalendar}
                enableBodyScroll={enableBodyScroll}
                calendarInfo={this.props.calendarInfo}
            />
        ) : (
            this.props.calendarInfo
        );
    }

    render() {
        const displayFormat = this.props.displayFormat || formats.CLIENT_DATE_FORMAT;
        const startDate = (this.props.startDate && this.props.startDate.format && this.props.startDate.format(displayFormat)) || '';
        const endDate = (this.props.endDate && this.props.endDate.format && this.props.endDate.format(displayFormat)) || '';
        return (
            <AsyncDateRangePicker
                startDateId={this.props.startId}
                endDateId={this.props.endId}
                startDate={this.props.disabled ? null : this.props.startDate}
                endDate={this.props.disabled ? null : this.props.endDate}
                disabled={this.props.disabled}
                startDatePlaceholderText={this.props.startPlaceholder}
                endDatePlaceholderText={this.props.endPlaceholder}
                onDatesChange={this.onDateChange}
                onFocusChange={this.onFocusChange}
                focusedInput={this.props.showCalendar ? this.state.focusedInput : null}
                withFullScreenPortal={this.mobile}
                orientation={this.mobile ? 'verticalScrollable' : 'horizontal'}
                numberOfMonths={this.mobile ? 6 : 2}
                minimumNights={0}
                initialVisibleMonth={this.mobile ? () => moment() : null}
                anchorDirection={this.props.anchorDirection}
                keepOpenOnDateSelect={this.mobile}
                daySize={this.mobile ? adjustedCalendarWidth() : 39}
                calendarInfoPosition={this.calendarInfoPosition()}
                renderCalendarInfo={this.renderCalendarInfo.bind(this)}
                customCloseIcon={<div />}
                readOnly={this.mobile}
                phrases={calendarPhrases()}
                displayFormat={() => displayFormat}>
                <DateRangeCalendarPlaceholder
                    startId={this.props.startId}
                    endId={this.props.endId}
                    startPlaceholder={this.props.startPlaceholder}
                    endPlaceholder={this.props.endPlaceholder}
                    startDate={startDate}
                    endDate={endDate}
                />
            </AsyncDateRangePicker>
        );
    }
}

DateRangeCalendar.propTypes = {
    startId: PropTypes.string.isRequired,
    endId: PropTypes.string.isRequired,
    startPlaceholder: PropTypes.string.isRequired,
    endPlaceholder: PropTypes.string.isRequired,
    onFocusChange: PropTypes.func.isRequired,
    onStartDateChange: PropTypes.func.isRequired,
    onEndDateChange: PropTypes.func.isRequired,
    startDate: PropTypes.object, // moment instance
    endDate: PropTypes.object, // moment instance
    disabled: PropTypes.bool,
    resetDepartureDate: PropTypes.func.isRequired,
    resetReturnDate: PropTypes.func.isRequired,
    anchorDirection: PropTypes.string,
    showCalendar: PropTypes.bool,
    pushUrlContext: PropTypes.func,
    popUrlContext: PropTypes.func,
    texts: PropTypes.object,
    displayFormat: PropTypes.string,
    calendarInfo: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

DateRangeCalendar.defaultProps = {
    startDate: null,
    endDate: null,
    disabled: false,
    anchorDirection: 'left',
};
